import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { Box, Typography, Button, Grid, List, ListItem, ListItemText, Paper, TextField, InputAdornment, Snackbar, Alert } from "@mui/material";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import SearchIcon from "@mui/icons-material/Search";
import { AuthContext } from "../../context/AuthContext";
import { getAllDeviceInfo, getDeviceInfo, getProfileInfo } from "../../services/api";
import DirectionsIcon from '@mui/icons-material/Directions';
import { useNavigate } from "react-router-dom";
import RouteIcon from "@mui/icons-material/Route";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import LocationOnIcon from "@mui/icons-material/LocationOn";

// Minimal icons to avoid missing icon issues
const defaultTruckIcon = new L.Icon({
    iconUrl: "https://img.icons8.com/emoji/48/000000/green-circle-emoji.png",
    iconSize: [20, 20],
    iconAnchor: [15, 30],
    popupAnchor: [0, -28]
});

// Map controller that recenters smoothly when selectedDevice changes
const MapController = ({ selectedDevice }) => {
    const map = useMap();
    const lastPos = useRef(null);

    useEffect(() => {
        if (!selectedDevice?.latitude || !selectedDevice?.longitude) return;

        const newPos = [selectedDevice.latitude, selectedDevice.longitude];
        const prevPos = lastPos.current;
        lastPos.current = newPos;

        // Skip if no previous position yet
        if (!prevPos) {
            map.setView(newPos, 16);
            return;
        }

        // Calculate distance in meters (Haversine)
        const R = 6371e3;
        const [lat1, lon1] = prevPos;
        const [lat2, lon2] = newPos;
        const dLat = ((lat2 - lat1) * Math.PI) / 180;
        const dLon = ((lon2 - lon1) * Math.PI) / 180;
        const a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos(lat1 * Math.PI / 180) *
            Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) ** 2;
        const distance = 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        // Only move map if device moved >5 m
        if (distance > 5) {
            try {
                map.flyTo(newPos, map.getZoom() >= 16 ? map.getZoom() : 16, { animate: true, duration: 0.8 });
            } catch {
                map.setView(newPos);
            }
        }
    }, [selectedDevice, map]);

    return null;
};


const Trackdevice = () => {
    const { auth } = useContext(AuthContext);
    const [devices, setDevices] = useState([]); // list to choose from
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedDevice, setSelectedDevice] = useState(null); // full device object
    const [deviceDetails, setDeviceDetails] = useState(null); // live details from getDeviceInfo
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const pollingRef = useRef(null);
    const isFetchingRef = useRef(false);
    const navigate = useNavigate();

    // Fetch profile (optional) and device list on mount / auth change
    useEffect(() => {
        if (!auth) return;
        let mounted = true;
        (async () => {
            try {
                const list = await getAllDeviceInfo(auth);
                if (!mounted) return;
                // normalize list to minimal fields used in UI
                const normalized = (list || []).map(d => ({
                    id: d.deviceId,
                    deviceCode: d.deviceCode,
                    name: d.clientName || `Device ${d.deviceId}`,
                    latitude: d.latitude ?? null,
                    longitude: d.longitude ?? null,
                    batteryPercentage: d.batteryPercentage ?? "N/A",
                    lastReported: d.lastReported ?? null,
                    raw: d.raw ?? null,
                    shipmentId: Array.isArray(d.shipments) && d.shipments.length ? d.shipments[d.shipments.length - 1].id : null
                }));
                setDevices(normalized);
            } catch (err) {
                console.error("Error fetching devices:", err);
                setSnackbarMessage("Failed to fetch device list");
                setOpenSnackbar(true);
            }
        })();
        return () => { mounted = false; };
    }, [auth]);

    // helper to fetch live info for a device (immediate call)
    const fetchLiveDevice = useCallback(async (device) => {
        if (!device) return null;
        // Prevent concurrent fetches
        if (isFetchingRef.current) return null;
        isFetchingRef.current = true;
        try {
            // Prefer shipmentId if available else use device id as fallback (depends on backend)
            const idToUse = device.shipmentId || device.id;
            const info = await getDeviceInfo(auth, idToUse);
            isFetchingRef.current = false;
            if (!info) return null;
            const details = {
                latitude: info.latitude ?? device.latitude,
                longitude: info.longitude ?? device.longitude,
                deviceBatteryPercentage: info.deviceBatteryPercentage ?? device.batteryPercentage,
                speed: info.speed ?? "N/A",
                shock: info.shock ?? "N/A",
                altitude: info.altitude ?? "N/A",
                receivedAt: info.receivedAt ?? device.lastReported,
                raw: info.raw ?? device.raw
            };
            setDeviceDetails(details);
            setDeviceDetails(prev => {
                // Prevent unnecessary re-renders if position didn’t change
                if (prev && prev.latitude === details.latitude && prev.longitude === details.longitude) {
                    return prev; // no change
                }
                setSelectedDevice(p => (p ? { ...p, latitude: details.latitude, longitude: details.longitude } : p));
                return details;
            });
        } catch (err) {
            console.error("Error fetching device info:", err);
            setSnackbarMessage("Error fetching live device data");
            setOpenSnackbar(true);
            isFetchingRef.current = false;
            return null;
        }
    }, [auth]);

    // Start polling every 15s for currently selected device
    useEffect(() => {
        // Clear any existing poller
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }

        if (!selectedDevice) {
            // clear live details when device unselected
            setDeviceDetails(null);
            return;
        }

        // Immediately fetch once when selected
        fetchLiveDevice(selectedDevice);

        // Set interval (15s)
        pollingRef.current = setInterval(() => {
            // avoid overlapping fetches
            if (!isFetchingRef.current) {
                fetchLiveDevice(selectedDevice);
            }
        }, 15000);

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, [selectedDevice, fetchLiveDevice]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, []);

    const handleDeviceClick = (device) => {
        // If device has no coords, still allow selection and rely on live fetch to populate
        setSelectedDevice(device);
    };

    const routeToNavigator = () => {
        if (!deviceDetails && !selectedDevice) return;
        const deviceToSend = {
            lat: deviceDetails?.latitude ?? selectedDevice.latitude,
            lng: deviceDetails?.longitude ?? selectedDevice.longitude,
            alt: deviceDetails?.altitude ?? selectedDevice.altitude,
            deviceCode: selectedDevice.deviceCode,
            batteryPercentage: deviceDetails?.deviceBatteryPercentage ?? selectedDevice.batteryPercentage,
            shock: deviceDetails?.shock ?? "N/A",
            speed: deviceDetails?.speed ?? "N/A",
            lastReported: deviceDetails?.receivedAt ?? selectedDevice.lastReported,
            name: selectedDevice.name,
        };
        navigate('/navigator-panel', { state: { destination: deviceToSend } });
    };

    const filteredList = devices.filter(d =>
        d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (d.deviceCode || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 3, ml: 2 }}>📍 Track Device</Typography>

            <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                    <Paper sx={{ height: "80vh", display: "flex", flexDirection: "column", p: 2 }}>
                        <Typography variant="h6">📋 Device List</Typography>

                        <Box sx={{ position: "sticky", top: 0, backgroundColor: "white", zIndex: 10, pb: 1 }}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                placeholder="Search devices..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                sx={{ mb: 2 }}
                                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
                            />
                        </Box>

                        <Box sx={{ flex: 1, overflowY: "auto" }}>
                            {filteredList.length === 0 ? (
                                <Box sx={{ padding: 2, textAlign: "center", color: "gray" }}>No devices available</Box>
                            ) : (
                                <List>
                                    {filteredList.map(device => (
                                        <ListItem
                                            button
                                            key={device.id}
                                            onClick={() => handleDeviceClick(device)}
                                            sx={{
                                                backgroundColor: selectedDevice?.id === device.id ? "rgba(25,118,210,0.08)" : "transparent",
                                                borderLeft: selectedDevice?.id === device.id ? "4px solid #1976d2" : "none",
                                                cursor: "pointer"
                                            }}
                                        >
                                            <ListItemText primary={device.name} secondary={`📡 ${device.deviceCode}`} />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>

                    </Paper>
                </Grid>

                <Grid item xs={12} sm={9}>
                    <Box sx={{ width: "100%", height: "80vh", position: 'relative' }}>
                        <MapContainer center={[12.881276, 79.911454]} zoom={6} style={{ height: '100%', width: '100%' }}>
                            <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png" />

                            {/* MapController recenters when deviceDetails or selectedDevice updates */}
                            <MapController selectedDevice={deviceDetails ? { ...selectedDevice, latitude: deviceDetails.latitude, longitude: deviceDetails.longitude } : selectedDevice} />

                            {/* Only render marker when a device is selected and coords available */}
                            {selectedDevice && (deviceDetails?.latitude || selectedDevice.latitude) && (deviceDetails?.longitude || selectedDevice.longitude) && (
                                <Marker
                                    position={[deviceDetails?.latitude ?? selectedDevice.latitude, deviceDetails?.longitude ?? selectedDevice.longitude]}
                                    icon={defaultTruckIcon}
                                >
                                    <Popup>
                                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#1976d2" }}>
                                            📡 {selectedDevice.name}
                                        </Typography>

                                        <Grid container spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                            <Grid item xs={12}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ fontWeight: "bold", display: "flex", alignItems: "center" }}
                                                >
                                                    <RouteIcon fontSize="small" sx={{ mr: 0.5 }} /> Device Code: {selectedDevice.deviceCode}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography variant="caption" sx={{ display: "flex", alignItems: "center" }}>
                                                    <BatteryFullIcon fontSize="small" sx={{ mr: 0.5 }} />
                                                    Battery: {deviceDetails?.deviceBatteryPercentage ?? selectedDevice.batteryPercentage ?? "N/A"}%
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography variant="caption" sx={{ display: "flex", alignItems: "center" }}>
                                                    ⚡ Shock: {deviceDetails?.shock ?? "N/A"}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography variant="caption" sx={{ display: "flex", alignItems: "center" }}>
                                                    🚀 Speed: {deviceDetails?.speed ?? "N/A"} km/h
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography variant="caption" sx={{ display: "flex", alignItems: "center" }}>
                                                    ⛰️ Altitude: {deviceDetails?.altitude ?? "N/A"} m
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography variant="caption" sx={{ display: "flex", alignItems: "center" }}>
                                                    <LocationOnIcon fontSize="small" sx={{ mr: 0.5 }} />
                                                    {deviceDetails?.latitude && deviceDetails?.longitude
                                                        ? `${deviceDetails.latitude.toFixed(5)}, ${deviceDetails.longitude.toFixed(5)}`
                                                        : selectedDevice.latitude && selectedDevice.longitude
                                                            ? `${selectedDevice.latitude.toFixed(5)}, ${selectedDevice.longitude.toFixed(5)}`
                                                            : "N/A"}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography variant="caption">
                                                    Reported:&nbsp;
                                                    {deviceDetails?.receivedAt && !isNaN(new Date(deviceDetails.receivedAt))
                                                        ? new Date(deviceDetails.receivedAt).toLocaleString("en-IN", {
                                                            timeZone: "Asia/Kolkata",
                                                            year: "numeric",
                                                            month: "2-digit",
                                                            day: "2-digit",
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                            second: "2-digit",
                                                            hour12: true,
                                                        })
                                                        : selectedDevice.lastReported
                                                            ? new Date(selectedDevice.lastReported).toLocaleString("en-IN", {
                                                                timeZone: "Asia/Kolkata",
                                                                year: "numeric",
                                                                month: "2-digit",
                                                                day: "2-digit",
                                                                hour: "2-digit",
                                                                minute: "2-digit",
                                                                second: "2-digit",
                                                                hour12: true,
                                                            })
                                                            : "N/A"}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Popup>

                                </Marker>
                            )}

                        </MapContainer>

                        {/* Overlay when no device selected */}
                        {!selectedDevice && (
                            <div style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                background: "rgba(255,255,255,0.9)",
                                padding: "1.2rem 1.6rem",
                                borderRadius: 8,
                                boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                                zIndex: 1000,
                                textAlign: "center"
                            }}>
                                <div style={{ fontWeight: 700, marginBottom: 6 }}>🛰️ No device selected</div>
                                <div style={{ color: "#555" }}>Select a device from the list to start live tracking (updates every 15s)</div>
                            </div>
                        )}

                    </Box>
                </Grid>
            </Grid>

            <Snackbar open={openSnackbar} autoHideDuration={3500} onClose={() => setOpenSnackbar(false)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
                <Alert onClose={() => setOpenSnackbar(false)} severity="warning" variant="filled">{snackbarMessage}</Alert>
            </Snackbar>
        </Box>
    );
};

export default Trackdevice;
